<template>
  <div
    class="shipping-info tw-mb-[130px] sm:tw-mb-0"
    :class="{'md:tw-px-10 lg:tw-px-0': isOnboardingRedesign}"
  >
    <!-- Shipping address confirmation modal -->
    <b-modal
      v-model="showModal"
      hide-header
      hide-footer
      centered
      :content-class="{'dark-bg': !isOnboardingRedesign}"
      no-close-on-backdrop
      no-close-on-esc
      no-stacking
    >
      <template v-if="isOnboardingRedesign">
        <div class="tw-px-10 lg:tw-px-20 tw-mb-6">
          <h2 class="tw-text-2xl tw-mb-2">
            We are unable to verify your address.
          </h2>
          <p class="tw-mb-0">
            <strong>{{ shippingString }}</strong> - Are you sure you want to ship to this address?
          </p>
        </div>
        <div
          class="tw-bg-white -tw-mx-6 -tw-mb-6 tw-px-6 tw-py-6 tw-rounded-b-[0.5rem]"
          style="box-shadow: -8px -6px 23.3px 0px rgba(45, 43, 43, 0.05);"
        >
          <div class="tw-max-w-[300px] tw-mx-auto">
            <b-button
              class="slight-rounded"
              pill
              block
              variant="info"
              :disabled="buttonDisabled"
              @click="showModal = false"
            >
              Update Address
            </b-button>
            <b-button
              class="slight-rounded tw-border-[#312849] tw-text-[#312849]"
              pill
              block
              variant="outline-info"
              :disabled="buttonDisabled"
              @click="() => submitForm()"
            >
              Yes, Use this address
            </b-button>
          </div>
        </div>
      </template>
      <template v-else>
        <b-row class="mb-6">
          <b-col cols="12">
            <div class="icon-logo dark"></div>
          </b-col>
        </b-row>
        <div class="modal-card">
          <h2
            class="mb-3 heading color-inherit"
            style="font-size: 26px; line-height: 32px"
          >
            We are unable to verify your address.
          </h2>
          <p style="font-size: 0.8rem">
            {{ shippingString }}
          </p>
          <p class="mb-5">
            Are you sure you want to ship here?
          </p>
          <b-button
            class="slight-rounded"
            pill
            block
            variant="info"
            :disabled="buttonDisabled"
            @click="showModal = false"
          >
            Update Address
          </b-button>
          <b-button
            class="slight-rounded"
            pill
            block
            variant="outline-secondary"
            :disabled="buttonDisabled"
            @click="() => submitForm()"
          >
            Yes, it is correct
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal
      v-model="showUnavailableStateModal"
      hide-header
      hide-footer
      centered
      :content-class="{'dark-bg': !isOnboardingRedesign}"
      no-close-on-backdrop
      no-close-on-esc
      no-stacking
    >
      <template v-if="isOnboardingRedesign">
        <div class="tw-px-10 lg:tw-px-20 tw-mb-6">
          <h2 class="tw-text-2xl tw-mb-2">
            Winona is currently unavailable for {{ selectedState.name }}.
          </h2>
          <p>
            Due to state-by-state medical laws, Winona is unable to ship to this
            state as a primary residence.
          </p>
          <p>To continue, please enter residence in one of the following states:</p>
          <p>{{ states.filter(state => state.hasOwnProperty('supported') && state.supported).map(state => state.name).join(', ') }}</p>
        </div>
        <div
          class="tw-bg-white -tw-mx-6 -tw-mb-6 tw-px-6 tw-py-6 tw-rounded-b-[0.5rem]"
          style="box-shadow: -8px -6px 23.3px 0px rgba(45, 43, 43, 0.05);"
        >
          <div class="tw-max-w-[300px] tw-mx-auto">
            <b-button
              class="slight-rounded"
              pill
              block
              variant="info"
              @click="showUnavailableStateModal = false"
            >
              Close
            </b-button>
          </div>
        </div>
      </template>
      <template v-else>
        <h2
          class="mb-3 heading color-inherit"
          style="font-size: 26px; line-height: 32px"
        >
          Winona is currently unavailable for {{ selectedState.name }}.
        </h2>
        <p>
          Due to state-by-state medical laws, Winona is unable to ship to this
          state as a primary residence.
        </p>
        <p>To continue, please enter residence in one of the following states:</p>
        <ul>
          <div
            v-for="state in states"
            :key="state.id"
          >
            <li v-if="state.hasOwnProperty('supported')">
              {{ state.id }}
            </li>
          </div>
        </ul>
        <b-row>
          <b-col
            cols="4"
            offset="8"
          >
            <b-button
              class="slight-rounded"
              pill
              block
              variant="outline-secondary"
              @click="showUnavailableStateModal = false"
            >
              Close
            </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>

    <template v-if="isOnboardingRedesign">
      <h2 class="question-title with-subtext">
        Shipping Information
      </h2>
      <p
        v-if="onboardingVariant === 'E1'"
        class="tw-text-black tw-text-2xl tw-mt-8"
      >
        Please confirm the address where you would like your Winona products shipped.
        We ship USPS Priority unless otherwise requested.
      </p>
      <p
        v-else-if="['F3v1', 'F3v2'].includes(onboardingVariant)"
        class="tw-text-black tw-text-2xl tw-mt-8"
      >
        Please confirm the address where you would like your Winona products shipped.
      </p>
      <p
        v-else
        class="tw-text-lg tw-mb-4"
      >
        Please confirm the address where you would like your Winona products shipped.
      </p>
    </template>
    <template v-else>
      <h2 class="question-title with-subtext">
        Shipping Information &amp; Address
      </h2>
      <p
        class="sub-text"
      >
        Please confirm the address of where you would like your Winona products
        shipped. We ship USPS Priority unless otherwise requested.
      </p>
    </template>
    <b-row
      v-if="nonrx"
      class="mt-5"
    >
      <b-col
        cols="12"
        md="6"
      >
        <label
          for="firstname"
          class="input-label"
        >First Name*</label>
        <b-form-input
          v-model="user.firstname"
          class="fs-mask onboarding-input"
          name="firstname"
          placeholder="First Name"
        />
      </b-col>
      <b-col
        cols="12"
        md="6"
      >
        <label
          for="lastname"
          class="input-label"
        >Last Name*</label>
        <b-form-input
          v-model="user.lastname"
          class="fs-mask onboarding-input"
          name="lastname"
          placeholder="Last Name"
        />
      </b-col>
    </b-row>
    <b-row :class="isOnboardingRedesign ? `tw-mt-2` : 'mt-5'">
      <b-col
        cols="12"
        md="12"
        class="mb-3"
      >
        <label
          v-if="!isOnboardingRedesign"
          for="address1"
          class="input-label"
        >Street Address*</label>
        <vue-google-autocomplete
          id="map"
          ref="streetAddress"
          classname="form-control"
          :placeholder="isOnboardingRedesign ? 'Street Address *' : 'Street Address'"
          :country="['us', 'pr']"
          class="fs-mask onboarding-input"
          @placechanged="getAddressData"
          @inputChange="onStreetAddressInputChange"
          @change="onStreetAddressChange"
        />
      </b-col>
      <b-col
        cols="12"
        md="12"
        class="mb-3"
      >
        <label
          v-if="!isOnboardingRedesign"

          for="address2"
          class="input-label"
        >Apartment/Suite</label>
        <b-form-input
          v-model.trim="address2"
          class="fs-mask onboarding-input"
          name="address2"
          placeholder="Apartment/Suite"
          :formatter="removeAccents"
          @input="fromAutocomplete = false"
        />
      </b-col>
      <b-col
        cols="12"
        md="12"
        class="mb-3"
      >
        <label
          v-if="!isOnboardingRedesign"
          for="city"
          class="input-label"
        >City*</label>
        <b-form-input
          v-model.trim="answers.city"
          class="fs-mask onboarding-input"
          name="city"
          :placeholder="isOnboardingRedesign ? 'City *' : 'City'"
          :formatter="removeAccents"
          @input="fromAutocomplete = false"
        />
      </b-col>
      <b-col
        cols="12"
        :md="isOnboardingRedesign ? '12' : '6'"
        class="mb-3"
      >
        <label
          v-if="!isOnboardingRedesign"
          for="state"
          class="input-label"
        >State*</label>
        <b-form-select
          v-model.trim="selectedState"
          :options="stateDropdownOptions"
          class="fs-mask onboarding-input"
          :class="{
            'is-empty': !selectedState,
            'custom-input': newFlowVariant
          }"
          name="state"
          readonly
          :placeholder="isOnboardingRedesign ? 'State *' : 'State'"
          :formatter="removeAccents"
          @input="fromAutocomplete = false"
        />
        <div
          v-if="newFlowVariant"
          style="border: 0.5px solid #C7C7C7"
          class="tw-mt-2"
        ></div>
      </b-col>
      <b-col
        cols="12"
        :md="isOnboardingRedesign ? '12' : '6'"
        class="mb-3"
      >
        <label
          v-if="!isOnboardingRedesign"
          for="zipcode"
          class="input-label"
        >Zip Code*</label>
        <b-form-input
          v-model.trim="answers.zipcode"
          class="fs-mask onboarding-input"
          name="zipcode"
          :placeholder="isOnboardingRedesign ? 'Zip Code *' : 'Zip Code'"
          @input="fromAutocomplete = false"
        />
      </b-col>
      <b-col
        cols="12"
        md="12"
        class="mb-3"
      >
        <label
          v-if="!isOnboardingRedesign"
          for="phone"
          class="input-label"
        >Phone*</label>
        <b-form-input
          v-model.trim="phone"
          class="fs-mask onboarding-input"
          :class="{
            'custom-input': newFlowVariant
          }"
          name="phone"
          :placeholder="isOnboardingRedesign ? 'Phone Number *' : 'Phone Number'"
          :formatter="formatPhoneNumber"
          pattern="[0-9]*"
          maxlength="12"
        />
        <div
          v-if="newFlowVariant"
          style="border: 0.5px solid #C7C7C7"
          class="tw-mt-2"
        ></div>
      </b-col>
    </b-row>

    <OnboardingCTAContainer>
      <div
        class="tw-w-full"
        :class="{'tw-flex tw-gap-4 tw-flex-col md:tw-flex-row lg:tw-px-4 xl:tw-px-0': isOnboardingRedesign && canMakePayment, 'tw-flex tw-flex-col tw-items-center': isOnboardingRedesign && !canMakePayment}"
      >
        <b-button
          class="slight-rounded"
          :class="{'tw-mb-2 md:tw-mb-0 md:tw-max-w-[300px] tw-mx-auto tw-text-base md:tw-text-lg': isOnboardingRedesign}"
          pill
          block
          variant="info"
          :disabled="isOnboardingRedesign ? buttonDisabled || loading || hasEmptyFields : buttonDisabled || loading"
          @click="() => submitForm()"
        >
          {{ loading ? "Loading" : !saving ? "Continue" : "Saving" }}
        </b-button>
        <PaymentMethod
          :class="{'md:tw-max-w-[300px] tw-mx-auto tw-w-full !tw-pt-0': isOnboardingRedesign, 'tw-pt-4': !isOnboardingRedesign}"
          :total-price="totalPrice"
          @success="onPaymentMethodSuccess"
          @canMakePayment="canMakePayment = $event"
        />
      </div>
    </OnboardingCTAContainer>

    <div class="tw-text-sm md:tw-text-base lg:tw-text-lg tw-text-[#787878] tw-mt-2">
      By proceeding with my phone number, I authorize Winona to send SMS updates about my order and shipping status. Message frequency varies. Reply STOP to opt out at any time.
    </div>

    <div
      class="mt-5"
      :class="{'tw-flex': isOnboardingRedesign}"
    >
      <div
        v-if="isOnboardingRedesign"
        class="tw-text-lg tw-mr-1 tw-mt-1 md:tw-mt-1.5"
      >
        <svg
          width="15"
          height="14"
          viewBox="0 0 15 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.60547 13.9453C6.65299 13.9453 5.75521 13.763 4.91211 13.3984C4.07357 13.0339 3.33301 12.5303 2.69043 11.8877C2.04785 11.2451 1.54427 10.5046 1.17969 9.66602C0.815104 8.82292 0.632812 7.92513 0.632812 6.97266C0.632812 6.02018 0.815104 5.12467 1.17969 4.28613C1.54427 3.44303 2.04557 2.7002 2.68359 2.05762C3.32617 1.41504 4.06673 0.911458 4.90527 0.546875C5.74837 0.182292 6.64616 0 7.59863 0C8.55111 0 9.44889 0.182292 10.292 0.546875C11.1351 0.911458 11.8779 1.41504 12.5205 2.05762C13.1631 2.7002 13.6667 3.44303 14.0312 4.28613C14.3958 5.12467 14.5781 6.02018 14.5781 6.97266C14.5781 7.92513 14.3958 8.82292 14.0312 9.66602C13.6667 10.5046 13.1631 11.2451 12.5205 11.8877C11.8779 12.5303 11.1351 13.0339 10.292 13.3984C9.45345 13.763 8.55794 13.9453 7.60547 13.9453ZM7.60547 12.7832C8.41211 12.7832 9.16634 12.6328 9.86816 12.332C10.57 12.0312 11.1875 11.6165 11.7207 11.0879C12.2539 10.5547 12.6686 9.93717 12.9648 9.23535C13.2656 8.53353 13.416 7.7793 13.416 6.97266C13.416 6.16602 13.2656 5.41178 12.9648 4.70996C12.6641 4.00814 12.2471 3.39062 11.7139 2.85742C11.1852 2.32422 10.5677 1.90951 9.86133 1.61328C9.1595 1.3125 8.40527 1.16211 7.59863 1.16211C6.79199 1.16211 6.03776 1.3125 5.33594 1.61328C4.63411 1.90951 4.01888 2.32422 3.49023 2.85742C2.96159 3.39062 2.54688 4.00814 2.24609 4.70996C1.94987 5.41178 1.80176 6.16602 1.80176 6.97266C1.80176 7.7793 1.94987 8.53353 2.24609 9.23535C2.54688 9.93717 2.96159 10.5547 3.49023 11.0879C4.02344 11.6165 4.64095 12.0312 5.34277 12.332C6.0446 12.6328 6.79883 12.7832 7.60547 12.7832ZM7.59863 8.20312C7.24772 8.20312 7.06771 8.02311 7.05859 7.66309L6.96973 4.04688C6.96517 3.8737 7.01986 3.73014 7.13379 3.61621C7.25228 3.50228 7.40495 3.44531 7.5918 3.44531C7.77409 3.44531 7.92448 3.50456 8.04297 3.62305C8.16602 3.73698 8.22526 3.88053 8.2207 4.05371L8.11816 7.66309C8.11361 8.02311 7.94043 8.20312 7.59863 8.20312ZM7.59863 10.4248C7.39811 10.4248 7.22494 10.3564 7.0791 10.2197C6.93327 10.0785 6.86035 9.90983 6.86035 9.71387C6.86035 9.5179 6.93327 9.35156 7.0791 9.21484C7.22494 9.07357 7.39811 9.00293 7.59863 9.00293C7.79915 9.00293 7.97233 9.07129 8.11816 9.20801C8.264 9.34473 8.33691 9.51335 8.33691 9.71387C8.33691 9.91439 8.26172 10.083 8.11133 10.2197C7.96549 10.3564 7.7946 10.4248 7.59863 10.4248Z"
            fill="#1A6A72"
          />
        </svg>
      </div>

      <span :class="{'tw-text-sm md:tw-text-lg': isOnboardingRedesign}">
        Treatment will ship by USPS. If your address cannot receive USPS postage,
        please let your physician know.
      </span>
    </div>
    <ErrorLabel v-if="isOnboardingRedesign && showError">
      {{ showError }}
    </ErrorLabel>

    <SideModal
      :show="showSideModal"
      header-class="terms-title"
      body-class="terms-body"
      feature="address"
      @exit="hideSideModal()"
    >
      <template #header>
        <div :class="isOnboardingRedesign ? 'tw-w-full tw-px-4' : 'mt-5'">
          <template v-if="isOnboardingRedesign">
            <h2 class="question-title with-subtext">
              Shipping Information
            </h2>
            <p class="sub-text mb-0">
              Please confirm the address where you would like your Winona products shipped.
            </p>
          </template>
          <template v-else>
            <h2 class="question-title with-subtext">
              Shipping Information &amp; Address
            </h2>
            <p class="sub-text mb-0">
              Please confirm the address of where you would like your Winona
              products shipped. We ship USPS Priority unless otherwise requested.
            </p>
          </template>
        </div>
      </template>
      <template #body>
        <div :class="{'tw-flex tw-flex-col tw-justify-between': isOnboardingRedesign}">
          <div
            class="tw-h-[calc(100vh-450px)] tw-overflow-y-auto tw-mb-10"
            style="height: calc(100vh-450px);"
          >
            <w-checkbox
              v-for="(suggestedAddress, suggestedAddressKey) in suggestedAddresses"
              :id="`address${suggestedAddressKey}`"
              :key="suggestedAddressKey"
              v-model="selectedAddressSuggestion"
              :value="`${suggestedAddressKey}`"
              type="radio"
              has-content
              label=""
            >
              <small>
                {{ suggestedAddressKey === 0 ? "Original" : "Suggested" }} Address
              </small>

              <div class="mt-2">
                <p :class="{'tw-text-xl tw-mb-1': isOnboardingRedesign}">
                  {{ suggestedAddress.street }}
                </p>
                <p :class="{'tw-mb-1': isOnboardingRedesign}">
                  {{ suggestedAddress.city }}
                </p>
                <p :class="{'tw-mb-1': isOnboardingRedesign}">
                  {{ parseState(suggestedAddress.state) }}
                </p>
                <p :class="{'tw-mb-1': isOnboardingRedesign}">
                  {{ suggestedAddress.country }}
                </p>
                <p :class="{'tw-mb-0': isOnboardingRedesign}">
                  {{ suggestedAddress.zip }}
                </p>
              </div>
            </w-checkbox>
          </div>
          <div
            :class="{'tw-pb-24 md:tw-pb-0 tw-w-full lg:tw-max-w-[300px] lg:tw-mx-auto': isOnboardingRedesign, 'pb-5': !isOnboardingRedesign}"
          >
            <template v-if="isOnboardingRedesign">
              <b-button
                class="slight-rounded tw-border-[#312849] tw-text-[#312849]"
                pill
                block
                variant="outline-info"
                :disabled="buttonDisabled || loading"
                @click="showSideModal = false"
              >
                Back
              </b-button>
              <b-button
                class="slight-rounded"
                pill
                block
                variant="info"
                :disabled="
                  buttonDisabled || loading || selectedAddressSuggestion == ''
                "
                @click="onUseSuggestedAddress"
              >
                Use this Address
              </b-button>
            </template>
            <template v-else>
              <b-button
                class="slight-rounded"
                pill
                block
                variant="info"
                :disabled="
                  buttonDisabled || loading || selectedAddressSuggestion == ''
                "
                @click="onUseSuggestedAddress"
              >
                Use this Address
              </b-button>
              <b-button
                class="slight-rounded"
                pill
                block
                variant="link"
                :disabled="buttonDisabled || loading"
                @click="showSideModal = false"
              >
                Back
              </b-button>
            </template>
          </div>
        </div>
      </template>
    </SideModal>
    <div
      class="bg-overlay"
      @click="hideSideModal()"
    ></div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
// services
import { OnboardingService } from '@/services/onboarding.service';
import { LookUpService } from '@/services/lookup.service';

// mixins
import mixins from '@/components/form/mixins';
import analyticsMixin from '@/mixins/analytics';
import formValidation from '@/components/form/mixins';

// libs
import VueGoogleAutocomplete from 'vue-google-autocomplete';

// components
import SideModal from '@/components/partials/SideModal.vue';
import OnboardingCTAContainer from '@/components/onboarding/OnboardingCTAContainer';
import PaymentMethod from '@/components/onboarding/checkout/PaymentMethod.vue';
import ErrorLabel from '@/components/partials/ErrorLabel.vue';

export default {
  components: {
    ErrorLabel,
    VueGoogleAutocomplete,
    SideModal,
    OnboardingCTAContainer,
    PaymentMethod
  },
  mixins: [mixins, analyticsMixin, formValidation],
  props: {
    body: {
      type: Object,
      default: () => ({}),
    },
    buttonDisabled: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    nonrx: {
      type: Boolean,
      default: false,
    },
    onboardingflow: {
      type: String,
      default: 'flowA',
    },
    analyticsId: {
      type: String,
      default: '',
    },
    totalPrice: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      showError: '',
      loading: false,
      states: [],
      answers: {
        address1: '',
        city: '',
        country: 'US',
        state: '',
        zipcode: null,
        // allowsms: true,
        // optforpromotions: true,
      },
      user: {
        firstname: '',
        lastname: '',
        birthday: '',
        phoneno: null,
      },
      phone: '',
      address2: '',
      selectedState: '',
      address: '',
      fromAutocomplete: false,
      showModal: false,
      showSideModal: false,
      selectedAddressSuggestion: '',
      suggestedAddresses: [],
      showUnavailableStateModal: false,
      canMakePayment: false,
    };
  },
  computed: {
    ...mapState('global', ['isOnboardingRedesign']),
    ...mapState('onboarding', ['onboardingPatient']),
    onboardingVariant() {
      return this.onboardingPatient?.flow_variant;
    },
    newFlowVariant() {
      return ['E1', 'F3v1', 'F3v2'].includes(this.onboardingVariant);
    },
    stateDropdownOptions() {
      const states = [{ value: '', text: 'State *', disabled: true }];
      for (const state of this.states) {
        states.push({ value: state, text: state.name });
      }
      return states;
    },
    shippingString() {
      return `${this.answers.address1}, ${
        this.address2 ? this.address2 + ',' : ''
      } ${this.answers.city}, ${this.answers.state}`;
    },
    hasEmptyFields() {
      return (
        !this.answers.address1 ||
        !this.answers.city ||
        !this.answers.state ||
        !this.answers.zipcode ||
        !this.phone
      );
    },
  },
  watch: {
    'user.phoneno'() {
      this.phone = this.formatPhoneNumber(this.user.phoneno);
    },
    selectedState(val) {
      this.answers.state = val.id;
    },
  },
  async mounted() {
    window.scrollTo(0, 0);

    try {
      this.loading = true;
      const { data } = await LookUpService.getLookUp('state');
      const userData = await OnboardingService.getPatientOnboarding();
      await this.$_analytics_track(
        'Onboarding Screen Viewed',
        {
          customerId: this.analyticsId,
          screen: '5.2-A',
          firstname: this.body.firstname,
          lastname: this.body.lastname,
          onboardingflow: this.onboardingflow,
        },
      );
      if (userData.data.address1) {
        this.fromAutocomplete = true;
      }
      this.answers.allowsms = userData.data.allowsms;
      this.$nextTick(() => {
        this.$refs.streetAddress.update(userData.data.address1);
      });
      this.answers.address1 = userData.data.address1;
      this.address2 = userData.data.address2;
      this.answers.city = userData.data.city;
      this.answers.zipcode = userData.data.zipcode;
      this.user.firstname = userData.data.firstname;
      this.user.lastname = userData.data.lastname;
      this.states = data;
      const selectedState = this.states.find(
        state => state.id == userData.data.state
      );
      this.selectedState = selectedState;
      this.answers.state = this.selectedState;
      this.phone = userData.data.phoneno;
      this.user.phoneno = userData.data.phoneno;
      this.user.birthday = userData.data.birthday;
      // this.answers.optforpromotions = userData.data.optforpromotions;
      this.loading = false;
    } finally {
      this.loading = false;
    }
  },
  methods: {

    /**
     * When the location found
     * @param {Object} addressData Data of the found location
     * @param {Object} placeResultData PlaceResult object
     * @param {String} id Input container ID
     */
    async getAddressData(addressData) {
      this.address = addressData;
      this.fromAutocomplete = true;
      if (addressData.street_number) {
        this.answers.address1 = this.removeAccents(
          `${addressData.street_number} ${addressData.route}`
        );
      } else {
        this.answers.address1 = this.removeAccents(addressData.route);
      }
      if (addressData.country == 'Puerto Rico') {
        // Manual state search override
        const selectedState = this.states.find(state => state.id == 'PR');
        this.selectedState = selectedState;
      } else {
        const selectedState = this.states.find(
          state => state.id == addressData.administrative_area_level_1
        );

        // console.log(addressData.administrative_area_level_1);
        this.selectedState = selectedState;
      }
      this.answers.city = addressData.locality
        ? this.removeAccents(addressData.locality)
        : '';
      this.answers.zipcode = addressData.postal_code
        ? addressData.postal_code
        : '';
    },
    onStreetAddressChange() {
      this.$refs.streetAddress.update(
        this.removeAccents(this.answers.address1)
      );
    },
    onStreetAddressInputChange() {
      this.answers.address1 = this.removeAccents(
        this.$refs.streetAddress.autocompleteText
      );
      console.log(this.answers.address1);
    },
    async submitForm(paymentMethod) {
      this.$emit('setButtonDisable', true);

      // Check if state is available
      const selectedState = this.states.find(
        state => state.id == this.selectedState.id
      );
      if (!(selectedState.hasOwnProperty('supported') && selectedState.supported)) {
        this.showUnavailableStateModal = true;
        this.$emit('setButtonDisable', false);
        return;
      }

      if (this.address2 && this.address2 !== '')
        this.answers['address2'] = this.address2;
      if (this.nonrx) {
        const userAnswer = {
          firstname: this.user.firstname,
          lastname: this.user.lastname,
          phoneno: this.phone,
        };
        try {
          await OnboardingService.updatePatientOnboarding(
            5.1,
            userAnswer
          );
        } catch (err) {
          this.showSideModal = false;
          const id = this.analyticsId
            ? this.analyticsId
            : 'Could not get customer ID';
          const email = this.body.email
            ? this.body.email
            : 'Could not get customer email';
          this.$store.dispatch('sendErrorLog', {
            step: 'Checkout - Basic Info',
            cus_id: id,
            email: email,
            error_log: err,
          });
          this.$emit('setButtonDisable', false);
          if (this.isOnboardingRedesign) {
            this.showError = err;
          } else {
            this.$emit('showAlert', err);
          }
          return;
        }
      }
      this.$emit('setButtonDisable', false);
      const answers = { ...this.answers };
      answers.phoneno = this.phone;

      answers.state = this.selectedState.id;
      const index = parseInt(this.selectedAddressSuggestion);
      if (this.showModal || (this.showSideModal && index === 0)) {
        answers.overriddenaddress = true;
      } else {
        this.suggestedAddresses = [
          {
            street: this.answers.address1,
            city: this.answers.city,
            state: this.answers.state,
            country: this.answers.country,
            zip: this.answers.zipcode,
          },
        ];
      }
      this.$store.commit('global/SET_MODAL_STATE', false);
      this.$emit('nextStep', answers, paymentMethod);
    },
    hideSideModal() {
      this.suggestedAddresses = [];
      this.elementTag(null, false);
    },
    elementTag(style, show) {
      this.showSideModal = show;
      const htmlTag = document.documentElement;
      htmlTag.style.overflow = style;
      htmlTag.scrollTop = htmlTag.scrollHeight;
    },
    parseState(stateText, shorten) {
      if (!stateText) {
        return;
      }
      if (shorten) {
        if (stateText.length > 2) {
          const foundState = this.states.find(
            state => state.name == stateText
          );
          return foundState ? foundState.id : '';
        } else {
          return stateText;
        }
      }
      if (stateText.length > 2) {
        return stateText;
      } else {
        const foundState = this.states.find(state => state.id == stateText);
        return foundState ? foundState.name : '';
      }
    },
    onUseSuggestedAddress() {
      const index = parseInt(this.selectedAddressSuggestion);
      this.$refs.streetAddress.update(this.suggestedAddresses[index]?.street);
      this.answers.address1 = this.suggestedAddresses[index]?.street;
      this.answers.city = this.suggestedAddresses[index]?.city;
      this.selectedState = this.states.find(
        state =>
          state.id ==
          this.parseState(this.suggestedAddresses[index]?.state, true)
      );
      this.answers.state = this.selectedState?.name;
      this.answers.zipcode = this.suggestedAddresses[index]?.zip;
      this.submitForm();
    },
    formatPhoneNumber(phoneNumber) {
      let strippedNumber = phoneNumber.replace(/\D/g, '');
      if (strippedNumber.length === 11 && strippedNumber.startsWith('1')) {
        strippedNumber = strippedNumber.slice(1);
      }
      const cleaned = strippedNumber.replace(/^\+1\s*/, '');
      const formatted = cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
      return formatted;
    },
    onPaymentMethodSuccess(stripeEvent) {
      let isCountrySupported
      if (['J', '1225-onboarding-core-v1', 'K'].includes(this.onboardingVariant)) {
        isCountrySupported = ['US', 'PR'].includes(stripeEvent.shippingAddress.address.country)
      } else {
        isCountrySupported = ['US', 'PR'].includes(stripeEvent.shippingAddress.country)
      }
      if (!isCountrySupported) {
        if (this.isOnboardingRedesign) {
          this.showError = 'Unsupported country';
        } else {
          this.$emit('showAlert', 'Unsupported country');
        }
      }

      if (['J', '1225-onboarding-core-v1', 'K'].includes(this.onboardingVariant)) {
        this.answers.address1 = stripeEvent.shippingAddress.address.line1;
        this.answers.city = stripeEvent.shippingAddress.address.city;
        const selectedState = this.states.find(
          state => state.id == stripeEvent.shippingAddress.address.state
        );
        this.selectedState = selectedState;
        this.answers.state = this.selectedState;
        this.answers.country = stripeEvent.shippingAddress.address.country;
        this.answers.zipcode = stripeEvent.shippingAddress.address.postal_code;
        this.phone = this.formatPhoneNumber(stripeEvent.billingDetails.phone);
        this.submitForm(stripeEvent.paymentMethod);
      } else {
        this.answers.address1 = stripeEvent.shippingAddress.addressLine?.[0];
        this.answers.city = stripeEvent.shippingAddress.city;
        const selectedState = this.states.find(
          state => state.id == stripeEvent.shippingAddress.region
        );
        this.selectedState = selectedState;
        this.answers.state = this.selectedState;
        this.answers.country = stripeEvent.shippingAddress.country;
        this.answers.zipcode = stripeEvent.shippingAddress.postalCode;
        this.phone = this.formatPhoneNumber(stripeEvent.payerPhone);
        this.submitForm(stripeEvent.paymentMethod);
      }
    },
  },
};
</script>

<style scoped>
.shipping-info > .row > .col > label {
  margin-bottom: 0px;
}

.shipping-info > .row > .col > input {
  padding: 5px;
}
</style>

<style lang="scss" scoped>
label {
  font-size: 11px;

  // @include media-breakpoint-up(xxl) {
  //   font-size: (11px * 2) !important
  // }
}

.is-empty {
  color: #B1C2D9;
}

.form-control {
  padding-left: 0;
}
</style>

<style scoped>
.onboarding-input{
  padding-left: 1rem !important;
  padding-right: 1rem;
}
  .custom-input {
    background-color: #f3f3f3;
    border:0;
    border-radius: 8px;
    padding-top: 0;
    padding-bottom: 0;
    height: 32px;
  }

</style>
